import styled, { css } from 'styled-components';
import { baseVariables, toRem } from '../../styles';
import { OModalMediaType } from './OModalMedia.types';

/** Native dialog reset, responsive width, viewport max-height so controls stay visible. */
export const Wrapper = styled.dialog<{ $variant: OModalMediaType }>`
  position: relative;
  margin: auto;
  padding: 0;
  border: none;
  border-radius: var(--t-cards-border-radius);
  overflow: hidden;
  background-color: var(--t-brand-bg-color);
  box-shadow: 0 ${toRem(7)} ${toRem(20)} rgba(0, 0, 0, 0.25);
  width: min(calc(100vw - ${toRem(48)}), ${toRem(343)});
  max-block-size: calc(100vh - ${toRem(48)});
  max-block-size: calc(100dvh - ${toRem(48)});

  ${({ $variant }) =>
    $variant === OModalMediaType.Video
      ? css`
          display: block;
        `
      : css`
          display: flex;
          flex-direction: column;
        `}

  &:not([open]) {
    display: none;
  }

  &::backdrop {
    background-color: rgba(0, 0, 0, 0.5);
  }

  @media ${baseVariables.mediaQuery.md} {
    width: min(calc(100vw - ${toRem(48)}), ${toRem(720)});
  }

  @media ${baseVariables.mediaQuery.lg} {
    width: min(calc(100vw - ${toRem(48)}), ${toRem(1140)});
  }
`;

/** Header row shared by both variants: subheader + close button, truncated single-line title. */
export const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--general-spacer-04, 1.5rem);
  padding: var(--general-spacer-04, 1.5rem);
  background-color: var(--t-brand-bg-color);

  .o-modal-media__subheader {
    margin-inline-end: auto;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/** Video inverse scope: dark scrim token + white foreground for overlaid subheader. */
export const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  --t-color-scrim-100: rgba(0, 0, 0, 0.54);
  --t-brand-fg-color: #ffffff;
`;

/** Video header: HeaderBar overlaid on the media, transparent bg, scrim behind. */
export const SafeZone = styled(HeaderBar)`
  position: absolute;
  inset-block-start: 0;
  inset-inline: 0;
  z-index: 1;
  background-color: transparent;

  .o-modal-media-scrim {
    z-index: -1;
  }

  .o-modal-media__subheader {
    color: var(--t-brand-fg-color);
  }
`;

/** Shared media shell; z-index 0 keeps video scrim visible. iFrame forces 16:9; video ratio owned by AVideo. */
export const MediaContainer = styled.div<{ $variant: OModalMediaType }>`
  position: relative;
  z-index: 0;
  flex: 1;

  /* iFrame forces 16:9 and shrinks on short viewports; video ratio owned by AVideo (no ratio here — double-constraint clips controls). */
  ${({ $variant }) =>
    $variant === OModalMediaType.iFrame &&
    css`
      aspect-ratio: 16 / 9;
      min-block-size: 0;
    `}

  /* Cap AIframe wrapper to container height (iFrame DOM only). */
  .o-modal-media__iframe {
    max-block-size: 100%;
  }

  /* Cap AVideo to viewport; contain keeps controls visible (video DOM only). */
  .o-modal-media__video {
    max-block-size: calc(100vh - ${toRem(48)});
    max-block-size: calc(100dvh - ${toRem(48)});

    video {
      object-fit: contain;
    }
  }
`;
